import { KeyboardEvent, SyntheticEvent } from 'react'

import { MenuItemSelection } from '../Picker/DropdownMenu'
import { FocusItemValue } from '../Picker/useFocusItemValue'
import { PickerItem } from '../types'
import { BaseCheckPickerProps } from './CheckPicker.types'

interface UseCheckPickerSelectionParams<Item extends PickerItem> extends Pick<
  BaseCheckPickerProps<Item>,
  'disabled' | 'onChange' | 'onSelect' | 'onClean'
> {
  data: Item[]
  selectedValues: Item['value'][]
  setValue: (value: Item['value'][]) => void
  focusItemValue: FocusItemValue<Item>
  setFocusItemValue: (value: FocusItemValue<Item>) => void
  cleanable: boolean
}

interface CommitValueParams<Item extends PickerItem> {
  nextValue: Item['value'][]
  item: Item | undefined
  event: SyntheticEvent
}

interface ToggleValueParams<Item extends PickerItem> {
  selectedValues: Item['value'][]
  value: Item['value']
}

const withoutValue = <Item extends PickerItem>({
  selectedValues,
  value,
}: ToggleValueParams<Item>) =>
  selectedValues.filter(selectedValue => !Object.is(selectedValue, value))

export const useCheckPickerSelection = <Item extends PickerItem>({
  data,
  selectedValues,
  setValue,
  focusItemValue,
  setFocusItemValue,
  disabled,
  cleanable,
  onChange,
  onSelect,
  onClean,
}: UseCheckPickerSelectionParams<Item>) => {
  const handleClean = (event: SyntheticEvent) => {
    if (disabled || !cleanable) {
      return
    }
    setValue([])
    onClean?.(event)
    onChange?.([], event)
  }

  const commitValue = ({ nextValue, item, event }: CommitValueParams<Item>) => {
    setValue(nextValue)
    onSelect?.(nextValue, item, event)
    onChange?.(nextValue, event)
  }

  const handleMenuPressEnter = (event: KeyboardEvent<HTMLElement>) => {
    if (!focusItemValue) {
      return
    }
    const isSelected = selectedValues.some(selectedValue =>
      Object.is(selectedValue, focusItemValue),
    )

    commitValue({
      nextValue: isSelected
        ? withoutValue({ selectedValues, value: focusItemValue })
        : [...selectedValues, focusItemValue],
      item: data.find(item => Object.is(item.value, focusItemValue)),
      event,
    })
  }

  const handleItemSelect = ({ value, item, checked, event }: MenuItemSelection<Item>) => {
    setFocusItemValue(value)
    commitValue({
      nextValue: checked ? [...selectedValues, value] : withoutValue({ selectedValues, value }),
      item,
      event,
    })
  }

  return { handleClean, handleMenuPressEnter, handleItemSelect }
}
